// src/components/Steps/EvProfile/EvProfile.jsx
import React, { useState, useEffect, useRef } from 'react';
import { track } from '../../../telemetry/journey.js';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { FaLocationDot, FaPlug, FaBolt, FaDollarSign, FaReceipt } from 'react-icons/fa6';

import useElementHeight from '../../../hooks/useElementHeight.js';
import { formatCurrency } from '../../../utils/helpers.js';
import {
  getStationPhotoUrlWithCacheBust,
  resolveStationPhotoUrl,
} from '../../../utils/stationPhotos.js';
import { isPublicChargerIdentifier } from '../../../utils/chargerCode.js';
import './EvProfile.less';

import {
  ActionButtonWrapper,
  EvHeader,
  ChargerNotFound,
  ChargerMaintenance,
  StationPhoto,
  StatusPill,
  MinimumChargeNotice,
  Notice,
} from '../../index.js';

import {
  fetchStationDataById,
  fetchPublishedStations,
  fetchStationById,
  fetchStationPreview,
} from '../../../api/fetch.js';
import { normaliseStationMaintenance } from '../../../utils/stationMaintenance.js';
import ElipsisLoader from '../../Loaders/ElipsisLoader';
import { useSession } from '../../../context/SessionContext.jsx';
import { notify } from '../../../api/notify.js';
import { useToast } from '../../../context/ToastContext.jsx';
import { deriveChargerDecision } from './deriveChargerDecision.js';
import {
  describeConnectors,
  resolveConnectorRouting,
  noUsableConnectorMessage,
} from '../../../utils/connectorStatus.js';
import { STEPS } from '../../../constants/steps.js';
import { ACCOUNT_CHOICE_ENABLED } from '../../../config/features.js';

const useQuery = () => new URLSearchParams(useLocation().search);

const EvProfile = ({ handleIncreaseStep }) => {
  const query = useQuery();
  const navigate = useNavigate();
  const notifiedOnceRef = useRef(false);
  const { showToast } = useToast();

  const stationId = query.get('stationId');
  const route = query.get('route') || null;
  // Admin preview mode: when `?preview_token=…` is present the
  // profile loader hits the token-gated preview endpoint instead
  // of the public one. See fetchStationPreview + the backend
  // route at GET /api/stations/:stationId/preview.
  const previewToken = query.get('preview_token') || null;
  const isPreviewMode = !!previewToken;

  const sessionContext = useSession();
  const session = sessionContext.state;

  // refs so effects don't re-run when context functions change
  const setStationIdRef = useRef(sessionContext.setStationId);
  const setPublicIdRef = useRef(sessionContext.setPublicId);
  const setStationMetaRef = useRef(sessionContext.setStationMeta);
  const setStationDataRef = useRef(sessionContext.setStationData);
  const setConnectorIdRef = useRef(sessionContext.setConnectorId);
  const connectToStationRef = useRef(sessionContext.connectToStation);
  const setStepRef = useRef(sessionContext.setStep);
  const updateRef = useRef(sessionContext.update);

  setStationIdRef.current = sessionContext.setStationId;
  setPublicIdRef.current = sessionContext.setPublicId;
  setStationMetaRef.current = sessionContext.setStationMeta;
  setStationDataRef.current = sessionContext.setStationData;
  setConnectorIdRef.current = sessionContext.setConnectorId;
  connectToStationRef.current = sessionContext.connectToStation;
  setStepRef.current = sessionContext.setStep;
  updateRef.current = sessionContext.update;

  const [deviceLocal, setDeviceLocal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  // The detailed /api/fetch/station-data endpoint can fail (5xx,
  // network, timeout) even when the public directory lookup succeeded.
  // We must NOT silently present an enabled Get Started CTA in that
  // case — the public metadata lookup only proves the charger exists,
  // not that it is currently chargeable. `deviceFetchFailed` disables
  // the CTA and surfaces a retry, without hiding the metadata card.
  const [deviceFetchFailed, setDeviceFetchFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  // notFound is set when the scanned/queried station ID isn't in the
  // directory. We deliberately do NOT synthesize fallback/demo station
  // data — the user is shown ChargerNotFound and the charging flow is
  // blocked. See the fetch effect below.
  const [notFound, setNotFound] = useState(false);
  // Preview-mode error state: set when the admin's preview token is
  // missing / invalid / expired / for a different station. We
  // intentionally do NOT fall back to the normal public fetch when
  // this fires — that would defeat the whole point of scoping the
  // preview token, since an admin previewing an unpublished station
  // would silently see a "not found" instead of "preview expired".
  const [previewUnauthorized, setPreviewUnauthorized] = useState(false);
  const [photoCacheBust, setPhotoCacheBust] = useState(Date.now());
  // Guards the on-tap re-verify (see handleStartSessionClick) so a
  // double-tap doesn't fire the detailed fetch twice. Declared here
  // to keep hook order stable across early returns further down.
  const [verifyingOnDemand, setVerifyingOnDemand] = useState(false);
  // Synchronous mirror of `verifyingOnDemand` — React setState is
  // asynchronous, so two back-to-back clicks (or a programmatic
  // a11y double-invoke that races the render) would both see
  // `verifyingOnDemand === false` and both fire a fresh fetch.
  // Reading + writing the ref inside the click handler makes the
  // guard atomic against synchronous re-entrance.
  // Holds the IN-FLIGHT verification promise, not a boolean.
  //
  // A boolean ref plus `disabled: verifyingOnDemand` (React state) could
  // disagree: the ref is set synchronously, the state commits on the next
  // render. In that window the button rendered ENABLED while the handler would
  // silently `return` — a tap the customer made and the app discarded, with no
  // feedback and no retry. Measured at 3 in 100 on staging.
  //
  // Holding the promise means a tap that arrives during verification JOINS the
  // work already running instead of being dropped, so the window is harmless
  // rather than merely narrow. One customer action still produces at most one
  // verification and one navigation.
  const verificationRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);
  const { ref, height } = useElementHeight();

  if (!stationId) {
    return <ChargerNotFound />;
  }

  // ── QR-scan → ChargerEntry gate ─────────────────────────────────
  // Every physical QR sticker — GCEV or legacy — must pass through
  // the public-charger resolver before the charging flow starts,
  // otherwise a station that has been unpublished (network_removed)
  // silently renders as a normal "Unavailable" profile instead of
  // the dedicated "Not on the GreenCharge network" screen.
  //
  // TWO sticker shapes coexist in the wild:
  //
  //   1. GCEV — `/station?stationId=GCEV1234567&route=qr` (or the
  //      post-fix `/c/GCEV1234567` scheme).
  //   2. Legacy internal station_id — `/station?stationId=KTurSSgk&route=qr`
  //      (base62 shorts, manufacturer serials, dev IDs — printed
  //      before the GCEV format existed). See sw-ev-api
  //      `utils/publicId.js` for the resolver that maps these to
  //      their `stations.public_id` GCEV internally.
  //
  // Route both formats through `/c/:chargerCode` — ChargerEntry
  // calls `/api/public/chargers/:identifier`, which now accepts both
  // shapes and normalises legacy IDs to their GCEV before applying
  // any business rule. On `chargeable=true` ChargerEntry forwards
  // back to `/station` WITHOUT `route=qr`, so this guard fires
  // exactly once per scan and never loops.
  //
  // Non-QR entries (admin deep links, hand-typed URLs) are left
  // alone. Admin preview mode uses `?preview_token=…` and its own
  // token-gated endpoint, so it must never be forced through /c/
  // (which would reject an unpublished target).
  if (
    isPublicChargerIdentifier(stationId)
    && route === 'qr'
    && !isPreviewMode
  ) {
    return (
      <Navigate
        to={`/c/${encodeURIComponent(stationId)}`}
        replace
      />
    );
  }

  // Fetch station data from API and populate context
  useEffect(() => {
    if (!stationId) return;

    // A superseded run must not write state.
    //
    // This effect re-runs on [stationId, route, previewToken, retryTick] and had
    // no cleanup, so two runs could be in flight at once. Each resolves the
    // public code to an internal station_id and then fetches device data — but a
    // run whose public lookup comes back empty falls back to querying by the
    // PUBLIC id, which returns a payload with no station_status. Whichever
    // response landed LAST won.
    //
    // Observed on staging: the profile reached decision_kind "ready", then
    // ~100ms later a second, public-id-keyed response overwrote it with
    // "Unknown". The driver sat on "Checking status…" for 55+ seconds and Get
    // Started could not proceed. Roughly 3 in 100 attempts.
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      setIsDemoMode(false);
      setNotFound(false);
      setPreviewUnauthorized(false);

      try {
        // Handle QR notify - fail silently to not block user flow
        if (route === 'qr' && !notifiedOnceRef.current) {
          const ts = new Date().toLocaleString();
          try {
            await notify(stationId, ts);
            console.log('[QR Scan] Admin notification sent for station:', stationId);
          } catch (err) {
            // Silent fail - don't block user from continuing
            console.error('[QR Scan] Failed to send admin notification (continuing anyway):', err);
          }
          notifiedOnceRef.current = true;

          // Clean up URL by removing route=qr parameter
          const next = new URLSearchParams(query);
          next.delete('route');
          navigate({ search: next.toString() }, { replace: true });
        }

        // Set station ID initially (may be publicId from QR code — will be resolved below)
        setStationIdRef.current(stationId);

        let station = null;
        let isUnpublished = false;

        // ── Admin preview path ────────────────────────────────────
        // If a preview token is on the URL we exclusively hit the
        // token-gated endpoint. We never fall back to the public
        // list on failure — otherwise an admin previewing an
        // unpublished station would silently see "not found"
        // instead of a clean "preview expired" state.
        if (isPreviewMode) {
          const previewResult = await fetchStationPreview(stationId, previewToken);
          if (!previewResult.ok) {
            setPreviewUnauthorized(true);
            setLoading(false);
            return;
          }
          const stationResponse = previewResult.station;
          const vd = stationResponse.verified_data || {};
          const nd = stationResponse.network_data || {};
          station = {
            station_id: nd.station_id || stationId,
            public_id: nd.public_id || null,
            display_name: vd.display_name || nd.display_name,
            location_text: vd.location_text,
            connector_type: vd.connector_type,
            max_power_kw: vd.max_power_kw,
            photo_name: vd.photo_name,
            // Preserve the API-derived URL. Since sw-ev-api commit
            // d2b0749, verified_data.photo_url is a fully-resolvable
            // URL for canonical GCS keys (never encodes slashes into
            // /static/stations/). Copying it forward here lets
            // resolveStationPhotoUrl skip the client-side URL
            // build entirely.
            photo_url: vd.photo_url,
            rate_per_kwh: vd.rate_per_kwh,
            rate_per_minute: vd.rate_per_minute,
            billing_unit: vd.billing_unit,
            session_fee: vd.session_fee,
            min_charge_amount: vd.min_charge_amount,
            published: vd.is_published,
            is_verified: stationResponse.is_verified,
            // Administrative maintenance override — normalised through
            // the single canonical helper (utils/stationMaintenance.js)
            // so preview / direct / list responses agree on the shape.
            ...(() => {
              const m = normaliseStationMaintenance(stationResponse);
              return {
                maintenance_mode: m.maintenanceMode,
                maintenance_reason: m.maintenanceReason,
                maintenance_started_at: m.maintenanceStartedAt,
              };
            })(),
          };
          const resolvedInternalId = nd.station_id;
          if (resolvedInternalId && resolvedInternalId !== stationId) {
            setStationIdRef.current(resolvedInternalId);
          }
          // Skip the demo-mode toast in preview mode — the admin
          // banner above the card is the clearer signal.
        } else {
        // Try to fetch the station directly first (handles unpublished stations)
        try {
          const stationResponse = await fetchStationById(stationId);
          // API returns { ok, verified_data, network_data, ... } — flatten into unified station object
          if (stationResponse?.ok) {
            const vd = stationResponse.verified_data || {};
            const nd = stationResponse.network_data || {};
            // Administrative maintenance override — same canonical
            // normaliser as the preview branch. THIS WAS THE BUG: the
            // non-preview path is the primary QR / deep-link fetch,
            // and it never copied maintenance_mode into `station`.
            // Consumers downstream (the maintenance render gate,
            // stationMeta, ChargeSetup guard) therefore never saw the
            // flag on a real QR scan and the blocked screen never
            // rendered until an admin flipped it back off. See
            // audit note in project memory.
            const maintenance = normaliseStationMaintenance(stationResponse);
            station = {
              station_id: nd.station_id || stationId,
              public_id: nd.public_id || null,
              display_name: vd.display_name || nd.display_name,
              location_text: vd.location_text,
              connector_type: vd.connector_type,
              max_power_kw: vd.max_power_kw,
              photo_name: vd.photo_name,
              // API-derived resolvable URL (canonical GCS URLs for
              // canonical keys). See the preview branch above for
              // the full rationale.
              photo_url: vd.photo_url,
              rate_per_kwh: vd.rate_per_kwh,
              rate_per_minute: vd.rate_per_minute,
              billing_unit: vd.billing_unit,
              session_fee: vd.session_fee,
              min_charge_amount: vd.min_charge_amount,
              published: vd.is_published,
              is_verified: stationResponse.is_verified,
              maintenance_mode: maintenance.maintenanceMode,
              maintenance_reason: maintenance.maintenanceReason,
              maintenance_started_at: maintenance.maintenanceStartedAt,
            };
            console.log('Fetched station directly:', station);

            // CRITICAL FIX: If the URL contained a publicId (GCEV format), the API
            // resolves it and returns the internal station_id. We MUST update context
            // to the internal ID so the SSE subscription topic matches the backend
            // publish topic (station:<internal_id>). Without this, the user app
            // subscribes to station:GCEV0920879 but the backend publishes to
            // station:AU105ACG00130L → topic mismatch → no real-time updates.
            const resolvedInternalId = nd.station_id;
            if (resolvedInternalId && resolvedInternalId !== stationId) {
              console.log('[EvProfile] Resolved publicId → internal station_id:', stationId, '→', resolvedInternalId);
              setStationIdRef.current(resolvedInternalId);
            }

            // Check if this station is unpublished/unclaimed.
            // The demo-mode toast is intentionally suppressed under an
            // admin preview token: preview mode's whole purpose is to
            // let an admin view an unpublished/inactive station, so
            // the "not fully configured yet" copy is confusing and the
            // "Admin Preview" banner above the card is the clearer
            // signal. Guarded explicitly here (not just via the outer
            // else) so a future refactor can't silently reintroduce
            // the demo toast during preview.
            if (!station.published || !stationResponse.is_verified) {
              isUnpublished = true;
              if (!isPreviewMode) {
                setIsDemoMode(true);
                showToast({
                  message: 'Charger is not fully configured yet (demo mode)',
                  type: 'info',
                  duration: 5000
                });
              }
            }
          }
        } catch (err) {
          console.warn('Direct station fetch failed, trying published list:', err);
        }

        // Fallback: Try to find in published stations list
        if (!station) {
          try {
            const stationsResponse = await fetchPublishedStations();
            console.log('Fetched published stations:', stationsResponse);

            const found = stationsResponse?.stations?.find(s => s.station_id === stationId);

            if (found) {
              // Publish-list rows carry maintenance_mode at the top
              // level (see sw-ev-api listStations DTO). Normalise
              // through the same helper as the direct fetch so the
              // render gate reads the same shape regardless of which
              // path succeeded.
              const maintenance = normaliseStationMaintenance(found);
              station = {
                ...found,
                maintenance_mode: maintenance.maintenanceMode,
                maintenance_reason: maintenance.maintenanceReason,
                maintenance_started_at: maintenance.maintenanceStartedAt,
              };
              console.log('Found station in published list:', station);
            }
          } catch (err) {
            console.error('Failed to fetch published stations:', err);
          }
        }
        } // end !isPreviewMode

        // Station not found in the directory — DO NOT synthesize fallback
        // data. Surface ChargerNotFound and stop the charging flow. The
        // operator can re-scan once the sticker has been activated through
        // the installer wizard.
        if (!station) {
          console.warn(`[EvProfile] Station ${stationId} not registered or not activated — blocking flow.`);
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Fetch device data for real-time status.
        //
        // If this call fails we DO NOT silently render Get Started as
        // if the charger were available — the public directory only
        // proves the charger exists, not that it is currently
        // chargeable. Track the failure so the CTA is disabled and
        // the customer sees "Unable to verify availability" with a
        // retry. Metadata (name, address, connector, rate) stays
        // visible because it came from the successful public lookup.
        const deviceQueryId = station?.station_id || stationId;
        let deviceData = null;
        try {
          deviceData = await fetchStationDataById(deviceQueryId);
          if (cancelled) return;
          console.log('Fetched device data:', deviceData);
          setDeviceLocal(deviceData);
          setStationDataRef.current(deviceData);
          setDeviceFetchFailed(false);
        } catch (err) {
          console.warn('Failed to fetch device data:', err);
          setDeviceFetchFailed(true);
        }

        // Extract metadata with fallbacks for missing/unpublished data
        const meta = {
          name: station.display_name || stationId,
          location: station.location_text || 'Location not set',
          connector: station.connector_type || 'Type 2',
          maxPower: station.max_power_kw ? `${station.max_power_kw} kW` : '22 kW',
          photoName: station.photo_name || null,
          // API-provided resolvable URL wins over any client-side
          // rebuild. When present this is what StationPhoto renders;
          // photoName remains available as the fallback path.
          photoUrl: station.photo_url || null,
          chargeCostRate: parseFloat(station.rate_per_kwh) || 0.5,
          // Pricing information (may be null for unpublished/unclaimed)
          ratePerKwh: parseFloat(station.rate_per_kwh) || null,
          ratePerMinute: station.rate_per_minute != null ? parseFloat(station.rate_per_minute) : null,
          billingUnit: station.billing_unit || 'kwh',
          sessionFee: parseFloat(station.session_fee) || 0,
          minChargeAmount: parseFloat(station.min_charge_amount) || 0,
          // Legacy alias kept for AmountPicker compatibility
          minCharge: parseFloat(station.min_charge_amount) || 0,
          // Administrative maintenance override — carried through the
          // session context so downstream steps (ChargeSetup) can
          // short-circuit before hitting the API if the flag flipped
          // on between EvProfile mount and the customer tapping Start.
          // Normalised through the shared helper so this reads the
          // same shape regardless of which fetch path won.
          ...(() => {
            const m = normaliseStationMaintenance(station);
            return {
              maintenanceMode: m.maintenanceMode,
              maintenanceReason: m.maintenanceReason,
              maintenanceStartedAt: m.maintenanceStartedAt,
            };
          })(),
        };
        
        console.log('Extracted metadata:', meta);
        setStationMetaRef.current(meta);

        // Set public charger ID (GCEV format) if available
        if (station.public_id) {
          setPublicIdRef.current(station.public_id);
        }

        // Update cache bust when photo changes
        setPhotoCacheBust(Date.now());
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch station data:', error);
        setError(error);
        showToast({
          message: "Couldn't load charger details. Please retry.",
          type: 'error',
          duration: 5000
        });
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, route, previewToken, retryTick]);

  const evMeta = session.stationMeta;
  const device = session.stationData || deviceLocal;

  const live = session.live || {};

  // Single source of truth for the status pill AND the click-time
  // verification verdict — see deriveChargerDecision.js.
  //
  // NOTE: this decision does NOT drive the CTA label or disabled
  // state. The CTA is INVARIANT ("Get Started", always enabled)
  // per product rule. Authoritative validation happens on tap,
  // against a FRESH fetch, in handleStartSessionClick below.
  const decision = deriveChargerDecision({ live, device, deviceFetchFailed });
  const { status } = decision;

  // Journey telemetry: what the driver was shown (kind only, no copy text).
  const renderedDecision = decision.kind === 'ready'
    && (device?.station_status || []).some((row) => row?.classification === 'PRECONNECTED_AVAILABLE')
    ? 'preconnected' : decision.kind;
  React.useEffect(() => {
    if (!deviceFetchFailed && !device) return; // still loading
    track('charger.decision.rendered', { result: renderedDecision || 'unknown' });
  }, [renderedDecision, deviceFetchFailed, !!device]);

  // ── Physical connectors on this charger ───────────────────────
  //
  // `device.station_status` is the per-connector OCPP status list.
  // Connector 0 is the OCPP charge-point-wide row and MUST NOT be
  // presented to the customer as a selectable physical connector.
  // `describeConnectors` filters connector 0 (the OCPP charge-point-wide
  // row), drops malformed rows and sorts ascending, so display order is
  // stable regardless of DB insertion order. Identity always travels as
  // `connectorId` — never as an array index.
  //
  // NOTE: this profile screen does NOT ask the driver to choose. It is an
  // overview. The connector decision happens on tap (see
  // handleStartSessionClick), against a FRESH fetch.
  const physicalConnectors = React.useMemo(
    () => describeConnectors(device?.station_status),
    [device?.station_status],
  );
  const selectedConnectorId = session.connectorId;

  // Drop a selection that does not belong to the charger currently on
  // screen — e.g. the driver scanned a different station mid-flow. We
  // never silently retarget to another socket; the decision is re-made
  // on the next tap.
  useEffect(() => {
    if (selectedConnectorId == null) return;
    if (physicalConnectors.length === 0) return;
    const stillPresent = physicalConnectors.some(
      (c) => c.connectorId === selectedConnectorId,
    );
    if (!stillPresent) {
      setConnectorIdRef.current(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physicalConnectors, selectedConnectorId]);

  // Kept for the click-handler branches below that still need
  // to know about maintenance specifically (e.g. surfacing the
  // maintenance toast even when derived is null).
  const maintenanceMode = device?.derived?.maintenanceMode === true;

  // Debug logging for status resolution
  const apiStatus = device?.station_status?.[0]?.status;
  if (live.status && apiStatus && live.status !== apiStatus && decision.kind !== 'in-use') {
    console.warn('[STATUS] Mismatch detected (using API status):', {
      live_status: live.status,
      api_status: apiStatus,
      tx_id: live.tx_id,
      resolved: status
    });
  }

  console.log('[STATUS DEBUG]', {
    live_status: live.status || 'null',
    api_status: apiStatus || 'null',
    resolved_status: status,
    badge_label: decision.badgeLabel,
    decision_kind: decision.kind,
    can_attempt_start: decision.canAttemptStart,
    block_reason: decision.blockReason,
  });

  // Resolve the photo URL to render.
  //
  // Priority:
  //   1. API-provided photoUrl on evMeta (winning path — since
  //      sw-ev-api commit d2b0749 this is a fully-resolvable
  //      https://storage.googleapis.com/... URL for canonical GCS
  //      keys, so nothing about the object key needs URL-encoding).
  //   2. Fallback: client-side resolution from photoName via the
  //      shape-aware helper. For canonical keys the helper routes
  //      through VITE_STATION_MEDIA_PUBLIC_BASE_URL or
  //      VITE_STATION_MEDIA_BUCKET; for legacy filenames it uses
  //      /static/stations/. Cache-busting is applied only for
  //      mutable legacy names — canonical keys are immutable by
  //      construction.
  //
  // `photoCacheBust` is still fed to the wrapper so a same-name
  // legacy re-upload still round-trips through a fresh fetch when
  // the parent explicitly bumps it. It is a no-op for canonical
  // keys because the helper skips the query-string append.
  const photoUrl = resolveStationPhotoUrl({
    photoUrl: evMeta?.photoUrl,
    photoName: evMeta?.photoName,
  }) || (evMeta?.photoName
    ? getStationPhotoUrlWithCacheBust(evMeta.photoName, photoCacheBust)
    : null);

  // Preview-unauthorised state takes priority when a preview_token
  // is on the URL. We deliberately DO NOT fall back to the public
  // fetch — an admin whose token has expired should see a clean
  // "preview expired" state, never the public "demo mode" toast or
  // a silent "not found" for an unpublished station.
  if (previewUnauthorized) {
    return (
      <div className="ev-content-wrapper">
        <EvHeader
          headerAction={{ label: 'Admin preview', action: null }}
        />
        <div className="ev-inner-profile-wrapper">
          <Notice
            variant="warning"
            title="Preview link expired"
            role="alert"
          >
            This admin preview link is no longer valid. Return to the
            admin dashboard and open the preview again to get a fresh
            link.
          </Notice>
        </div>
      </div>
    );
  }

  // Not-found state takes priority — set when the directory has no
  // matching station for the scanned ID. Production never falls back
  // to demo data.
  if (notFound) {
    return <ChargerNotFound chargerCode={stationId} />;
  }

  // Administrative maintenance override. Priority runs above
  // Available / Online / connector-level availability — a charger may
  // still be technically online, but customers must see it as
  // unavailable for use. Rendered before the profile card so the
  // normal Start / connector / payment controls never mount.
  //
  // We resolve through the canonical helper across every source we
  // hold at this point: the session context (populated by the setter
  // effect once the fetch settles), the live device row (its own
  // top-level maintenance_* fields), and the local device copy (same
  // shape as `device` but tolerates the moment before setStationData
  // has propagated). Any TRUE result wins.
  const maintenanceSources = [
    session.stationMeta ? {
      maintenanceMode: session.stationMeta.maintenanceMode,
      maintenanceReason: session.stationMeta.maintenanceReason,
      maintenanceStartedAt: session.stationMeta.maintenanceStartedAt,
    } : null,
    device || null,
    deviceLocal || null,
  ];
  let maintenanceHit = null;
  for (const src of maintenanceSources) {
    const m = normaliseStationMaintenance(src);
    if (m.maintenanceMode) {
      maintenanceHit = m;
      break;
    }
  }
  if (maintenanceHit) {
    return (
      <ChargerMaintenance
        chargerCode={stationId}
        reason={maintenanceHit.maintenanceReason}
      />
    );
  }

  // Show error state if fetch failed (network / 500 / etc.)
  if (error) {
    return <ChargerNotFound chargerCode={stationId} />;
  }

  // ── CTA is INVARIANT (with a multi-connector selection gate) ─
  //
  // Product rule (from the "CTA implementation is still wrong"
  // brief): the primary CTA always reads "Get Started" and stays
  // enabled — regardless of derived charger status. The button is
  // an INTERACTION AFFORDANCE, not permission to bypass backend
  // validation. Authoritative validation happens on tap against a
  // fresh fetch (see handleStartSessionClick below).
  //
  // The button is disabled ONLY during the in-flight verify
  // request, to prevent duplicate taps. During that window we
  // temporarily swap the label to "Checking charger…" so the tap
  // has visible feedback.
  //
  // The CTA is INVARIANT: it always reads "Get Started". The profile
  // is an overview and never asks for a connector, so there is no
  // "select a connector first" state to represent here — the connector
  // decision is made after the tap, against fresh data.
  const actions = [
    {
      id: 'start-session',
      label: verifyingOnDemand ? 'Checking charger…' : 'Get Started',
      type: 'btn-dark',
      disabled: verifyingOnDemand,
    },
  ];

  const handleStartSessionClick = async (actionId) => {
    if (actionId !== 'start-session') return;

    // Rapid or mid-flight taps JOIN the verification already running; they are
    // never dropped. Still exactly one verification and one navigation per
    // customer action — the joiner awaits the result and takes no further
    // action of its own, because the run it joined has already routed.
    if (verificationRef.current) {
      await verificationRef.current;
      return;
    }

    const verification = (async () => {
    setVerifyingOnDemand(true);
    const deviceQueryId = evMeta?.name && session.stationId
      ? session.stationId
      : stationId;

    // Fetch fresh canonical station data on every tap. The stale
    // in-memory `device` may reflect state from seconds or minutes
    // ago; the click-time fetch is the SOURCE OF TRUTH for whether
    // this charger can start a session RIGHT NOW.
    let freshDevice = null;
    let fetchFailed = false;
    try {
      freshDevice = await fetchStationDataById(deviceQueryId);
      setDeviceLocal(freshDevice);
      setStationDataRef.current(freshDevice);
      setDeviceFetchFailed(false);
    } catch (err) {
      fetchFailed = true;
      setDeviceFetchFailed(true);
      console.warn('[EvProfile] fresh device fetch failed on Get Started tap:', err);
    }

    // Re-derive against the fresh device. `canAttemptStart` +
    // `blockReason` are the load-bearing outputs; everything else
    // (badge, kind) is for observability.
    const nextDecision = deriveChargerDecision({
      live,
      device: freshDevice,
      deviceFetchFailed: fetchFailed,
    });

    // Clear the in-flight guard atomically wherever we return —
    // both the async state (drives label + disabled) and the
    // sync ref (blocks re-entrant clicks) must reset together.
    // Kept as a no-op so every early-return site reads unchanged. The real
    // reset happens in the finally below, which cannot be skipped by a throw
    // or by a path that forgets to call it — the previous version cleared at
    // six separate return sites and none of them were protected.
    const clearVerifying = () => {};

    // ── Station-level administrative gates ───────────────────
    // Maintenance, and a fetch we could not verify, are properties of
    // the CHARGER rather than of any single socket, so they block no
    // matter how many connectors look free.
    if (nextDecision.kind === 'maintenance' || nextDecision.kind === 'unverified') {
      showToast({
        message: nextDecision.blockReason || 'This charger is not ready to start a session right now.',
        type: 'error',
        duration: 4500,
      });
      clearVerifying();
      return;
    }

    // ── Single-connector chargers: the aggregate is exact ────
    //
    // When a charger has one physical connector the station-wide
    // `derived` block IS that connector's state, so the backend verdict
    // is authoritative and is respected unchanged. This keeps the
    // long-standing single-connector behaviour (and its block copy)
    // byte-for-byte identical.
    const freshConnectors = describeConnectors(freshDevice?.station_status);
    if (freshConnectors.length <= 1 && !nextDecision.canAttemptStart) {
      showToast({
        message: nextDecision.blockReason || 'This charger is not ready to start a session right now.',
        type: 'error',
        duration: 4500,
      });
      clearVerifying();
      return;
    }

    // Local pricing sanity check — protects against advancing
    // into a session where the operator forgot to publish a rate.
    // Applies once the fresh backend fetch has confirmed the
    // charger is chargeable.
    const billingUnit = evMeta?.billingUnit || 'kwh';
    const hasPricing = billingUnit === 'minute'
      ? (typeof evMeta?.ratePerMinute === 'number' && evMeta.ratePerMinute > 0)
      : (typeof evMeta?.ratePerKwh === 'number' && evMeta.ratePerKwh > 0);
    if (!hasPricing) {
      showToast({
        message: 'This charger is not ready to start a session yet. Please try again later.',
        type: 'error',
        duration: 4500,
      });
      clearVerifying();
      return;
    }

    // ── Connector routing ────────────────────────────────────
    //
    // Decided on USABLE connectors, not installed ones, and read from
    // the fresh fetch above.
    //
    // This deliberately overrides `deriveChargerDecision`'s verdict for
    // the operational states, because that helper AGGREGATES across
    // connectors: one socket charging makes the whole station report
    // "in use". On a multi-connector charger that aggregate must not
    // veto a driver who can still plug into a free socket. When nothing
    // is usable we fall back to the aggregate's wording so the existing
    // single-connector copy is preserved exactly.
    const routing = resolveConnectorRouting(freshConnectors);

    if (routing.kind === 'none') {
      const message = (!nextDecision.canAttemptStart && nextDecision.blockReason)
        ? nextDecision.blockReason
        : noUsableConnectorMessage(freshConnectors);
      showToast({ message, type: 'error', duration: 4500 });
      clearVerifying();
      return;
    }

    if (routing.kind === 'single') {
      // Exactly one usable connector — resolve it for the driver. No
      // selection screen is shown. The account question comes next either
      // way; `pendingConnectorRouting` tells it there is nothing to choose.
      setConnectorIdRef.current(routing.connectorId);
      if (ACCOUNT_CHOICE_ENABLED) updateRef.current({ pendingConnectorRouting: 'single' });
      clearVerifying();
      handleIncreaseStep();
      return;
    }

    // More than one usable connector: the driver must say which socket
    // they physically plugged into. A selection that is still usable is
    // preserved (it survives a login round-trip inside the same flow);
    // one that is not is cleared rather than silently retargeted.
    const keepsSelection = routing.usable.some(
      (c) => c.connectorId === session.connectorId,
    );
    if (!keepsSelection && session.connectorId != null) {
      setConnectorIdRef.current(null);
    }
    // Stash the decision instead of acting on it. The account question is
    // asked first — it must come before any session exists — and App's
    // forward routing consumes this on the way out of ACCOUNT_CHOICE.
    // Still click-time truth: ConnectorSelection re-validates staleness
    // when it renders, exactly as before.
    // With the account-choice screen off (rollout flag), go straight to the
    // connector choice, as before that screen existed.
    if (!ACCOUNT_CHOICE_ENABLED) {
      setStepRef.current(STEPS.CONNECTOR_SELECTION);
    } else {
      updateRef.current({ pendingConnectorRouting: 'choose' });
      setStepRef.current(STEPS.ACCOUNT_CHOICE);
    }
    })();

    verificationRef.current = verification;
    try {
      await verification;
    } finally {
      verificationRef.current = null;
      // An unmounted component must not setState; the ref is cleared either way
      // so a remounted instance starts clean.
      if (mountedRef.current) setVerifyingOnDemand(false);
    }
  };

  return (
    <div className="ev-content-wrapper">
      <EvHeader
        headerAction={{
          label: 'Charger overview',
          action: null,
        }}
      />

      {loading || (!device && !isPreviewMode && !deviceFetchFailed) ? (
        <>
          <div className="loading-container">
            <ElipsisLoader />
          </div>
        </>
      ) : (
        <div className="ev-inner-profile-wrapper">
          {isPreviewMode && (
            <Notice
              variant="info"
              title="Admin Preview"
              className="evp-admin-preview-banner"
              role="status"
            >
              This charger is not currently publicly available.
            </Notice>
          )}
          {/*
            Device-fetch failure notice. The public directory lookup
            succeeded (we have metadata below) but the detailed
            availability endpoint returned a 5xx / timed out. The
            CTA stays enabled (invariant "Get Started"); tapping it
            re-fetches fresh data and either advances or shows the
            block reason via toast. This inline notice is a
            proactive hint — the Retry button re-runs the initial
            effect without needing a tap on the primary CTA.
          */}
          {deviceFetchFailed && (
            <div data-testid="device-fetch-failed-notice">
              <Notice
                variant="warning"
                title="Unable to verify availability"
                role="alert"
                actions={
                  <button
                    type="button"
                    className="notice__action-btn"
                    onClick={() => {
                      setDeviceFetchFailed(false);
                      setRetryTick((n) => n + 1);
                    }}
                  >
                    Retry
                  </button>
                }
              >
                We couldn&apos;t reach the charger&apos;s live status.
                Metadata below is from the last known state — please
                retry before starting a session.
              </Notice>
            </div>
          )}
          <div className="evp-card">
            <StationPhoto
              photoUrl={photoUrl}
              alt={evMeta?.name || 'EV charger'}
              variant="hero"
            />

            <div className="evp-body">
              <p className="evp-eyebrow">Selected charger</p>
              <h2 className="evp-title">{evMeta?.name || 'EV Station'}</h2>

              {evMeta?.location && (
                <p className="evp-location">
                  <FaLocationDot size={16} className="evp-location-icon" />
                  {evMeta.location}
                </p>
              )}

              <div className="evp-status">
                <StatusPill status={status} />
              </div>

              <div className="evp-stats-grid">
                <div className="evp-stat">
                  <div className="evp-stat-icon">
                    <FaBolt size={18} />
                  </div>
                  <div className="evp-stat-text">
                    <div className="evp-stat-label">Max Power</div>
                    <div className="evp-stat-value">
                      {evMeta?.maxPower || '—'}
                    </div>
                  </div>
                </div>

                <div className="evp-stat">
                  <div className="evp-stat-icon">
                    <FaPlug size={18} />
                  </div>
                  <div className="evp-stat-text">
                    <div className="evp-stat-label">Connector</div>
                    <div className="evp-stat-value">
                      {evMeta?.connector || '—'}
                    </div>
                  </div>
                </div>

                {/* Pricing Information */}
                {evMeta?.billingUnit === 'minute' && evMeta?.ratePerMinute != null ? (
                  <div className="evp-stat">
                    <div className="evp-stat-icon evp-stat-icon--pricing">
                      <FaDollarSign size={18} />
                    </div>
                    <div className="evp-stat-text">
                      <div className="evp-stat-label">Rate / min</div>
                      <div className="evp-stat-value">
                        {formatCurrency(evMeta.ratePerMinute)}
                      </div>
                    </div>
                  </div>
                ) : evMeta?.ratePerKwh !== undefined && evMeta?.ratePerKwh !== null && (
                  <div className="evp-stat">
                    <div className="evp-stat-icon evp-stat-icon--pricing">
                      <FaDollarSign size={18} />
                    </div>
                    <div className="evp-stat-text">
                      <div className="evp-stat-label">Rate / kWh</div>
                      <div className="evp-stat-value">
                        {formatCurrency(evMeta.ratePerKwh)}
                      </div>
                    </div>
                  </div>
                )}

                {evMeta?.sessionFee !== undefined && evMeta?.sessionFee !== null && evMeta?.sessionFee > 0 && (
                  <div className="evp-stat">
                    <div className="evp-stat-icon evp-stat-icon--pricing">
                      <FaReceipt size={18} />
                    </div>
                    <div className="evp-stat-text">
                      <div className="evp-stat-label">Session Fee</div>
                      <div className="evp-stat-value">
                        {formatCurrency(evMeta.sessionFee)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Optional minimum charge display */}
              {evMeta?.minCharge !== undefined && evMeta?.minCharge !== null && evMeta?.minCharge > 0 && (
                <MinimumChargeNotice amount={formatCurrency(evMeta.minCharge)} />
              )}

              {/*
                No connector picker here. The profile is an OVERVIEW:
                the driver reviews the charger and taps Get Started.
                Connector choice is a separate step, shown only when
                the tap resolves more than one USABLE connector — see
                handleStartSessionClick and STEPS.CONNECTOR_SELECTION.
              */}

              <p className="evp-helper">
                When you're ready, tap <strong>Get Started</strong> to begin
                your charging session.
              </p>
            </div>
          </div>
        </div>
      )}

      <ActionButtonWrapper
        ref={ref}
        actions={actions}
        onClickAction={handleStartSessionClick}
      />
    </div>
  );
};

export default EvProfile;
